<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import AuthService from '@/api/services/auth.service';
  import { useGtag } from '@/composition/use-gtag';
  import { useUi } from '@/composition/use-ui';
  import { IUser } from '@/interfaces/IUser';
  import V2ShellNotice from './V2ShellNotice.vue';

  /**
   * Asks the owner to verify their email (plan §4.2); after sending, says
   * where the link went.
   */
  const props = defineProps<{ user: IUser }>();

  const authService = new AuthService();
  const gtag = useGtag();
  const ui = useUi();
  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;

  const sending = ref(false);
  const sent = ref(false);

  /** `fede****@gmail.com`: enough to recognise the address, not to read it. */
  const obfuscatedEmail = computed(() => {
    const [name, domain] = props.user.email.split('@');
    return `${name.slice(0, 4)}${'*'.repeat(
      Math.max(0, name.length - 4)
    )}@${domain}`;
  });

  async function verify() {
    sending.value = true;
    try {
      await authService.sendVerifyEmail();
      sent.value = true;
      gtag.event('sendVerify', {
        event_category: 'account',
        event_label: props.user.id,
      });
    } catch (error) {
      ui.serverError(error, String(proxy.$t('shell.verify.error')));
    } finally {
      sending.value = false;
    }
  }
</script>

<template>
  <V2ShellNotice
    v-if="!sent"
    tone="info"
    :title="$t('shell.verify.title')"
    :action-text="$t('shell.verify.action')"
    :action-loading="sending"
    @action="verify"
  />
  <V2ShellNotice
    v-else
    tone="info"
    :title="$t('shell.verify.sent', { email: obfuscatedEmail })"
    :help="$t('shell.verify.sentHelp')"
  />
</template>
